import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, delay } from 'rxjs';
import { EmailAction } from 'src/app/enums/EmailAction';
import { NavigationService } from 'src/app/services/navigation.service';
import { Api } from 'src/smoothr-web-app-core/api/api';
import { RepositoryService } from 'src/smoothr-web-app-core/services/repository/repository.service';
import { ValidationUtils } from 'src/smoothr-web-app-core/utils/validation-utils';
import { AppRestorePasswordModalComponent } from '../app-restore-password-modal/app-restore-password-modal.component';
import { AppComponent } from 'src/app/app.component';
import { AuthStrategy } from 'src/smoothr-web-app-core/models/AuthStrategy';

@Component({
	selector: 'app-sign-in-modal',
	templateUrl: './sign-in-modal.component.html',
	styleUrls: ['./sign-in-modal.component.scss'],
})
export class SignInModalComponent implements OnInit {
	loading = false;
	showPassword = false;
	isValidEmail = true;
	isValidPassword = true;
	requestError = false;
	private readonly subscription: Subscription;

	signInForm: FormGroup;
	email = 'email';
	password = 'password';

	constructor(
		private translate: TranslateService,
		private router: Router,
		private repository: RepositoryService,
		private snackbarCtrl: MatSnackBar,
		private alertCtrl: AlertController,
		private route: ActivatedRoute,
		private modalCtrl: ModalController,
		private fb: FormBuilder,
		private navigationService: NavigationService
	) {
		this.subscription = repository.customerAuth.subscribe(customerAuth => {
			if (customerAuth && customerAuth.customer && !customerAuth.customer.isAnonymous) {
				this.navigationService.account();
				this.subscription.unsubscribe();
			}
		});
	}
	static async show(modalCtrl: ModalController): Promise<any> {
		const modal = await modalCtrl.create({
			cssClass: AppComponent.largeScreen ? 'sign-modal' : 'menu-modal',
			component: SignInModalComponent,
			componentProps: {},
			showBackdrop: true,
			backdropDismiss: true,
		});
		await modal.present();
		const result = (await modal.onDidDismiss()).data;
		return result;
	}
	ngOnInit(): void {
		this.route.queryParams.subscribe(async params => {
			if (params.id && params.auth) {
				this.processOneTimeLoginToken(params.id, params.auth);
				return;
			}
			if (params.mode === EmailAction.RESET_PASSWORD && params.token) {
				await this.showNewPasswordDialog(params.token);
			}
		});

		this.createSignInForm();
	}

	createSignInForm() {
		const formControlNames: any = {};

		formControlNames[this.email] = [
			'',
			[
				Validators.required,
				Validators.pattern(
					"^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$"
				),
			],
		];
		formControlNames[this.password] = ['', [Validators.required]];

		this.signInForm = this.fb.group(formControlNames);
	}

	getFormControlValue(controlName?: string) {
		if (controlName) {
			return this.signInForm.get(controlName)?.value;
		}
		return '';
	}

	isValid(controlName?: string): boolean {
		if (controlName) {
			return this.signInForm.get(controlName)?.hasError('required') && this.signInForm.get(controlName)?.dirty;
		}
		return this.signInForm?.invalid;
	}

	isValidByPattern(controlName?: string): boolean {
		if (controlName) {
			return this.signInForm.get(controlName)?.errors && !this.signInForm.get(controlName)?.hasError('required');
		}
		return this.signInForm.invalid;
	}

	getErrorsForm(controlName) {
		if (controlName) {
			return this.signInForm.get(controlName).errors;
		}

		return null;
	}

	async processOneTimeLoginToken(id: string, auth: string) {
		this.loading = true;
		try {
			const res = (await Api.oneTimeLogin(id, auth)).data;
			this.repository.customerAuth.emit(res);
		} catch (e) {
			console.error(e);
		}
		this.loading = false;
	}

	ngOnDestroy() {
		if (this.subscription && !this.subscription.closed) {
			this.subscription.unsubscribe();
		}
	}

	signUp() {
		this.modalCtrl.dismiss('sign-up');
	}

	async signIn() {
		this.loading = true;
		try {
			const response = (await Api.signInWithCredentials(this.getFormControlValue('email'), this.getFormControlValue('password')))
				.data;
			this.repository.customerAuth.emit(response);
			this.modalCtrl.dismiss();
			if (history.state.redirectToOrder) {
				await this.navigationService.order(true);
			} else if (this.repository._order === null) {
				await this.navigationService.home();
			} else {
				await this.navigationService.menu();
			}
			this.snackbarCtrl.open(this.translate.instant('sign_in.success'), null, {
				duration: 5000,
			});
		} catch (e) {
			this.snackbarCtrl.open(this.translate.instant('sign_in.error'), null, {
				duration: 5000,
			});
			this.requestError = true;
			console.error(e);
		}
		this.loading = false;
	}

	async showResetPasswordDialog() {
		const response = await AppRestorePasswordModalComponent.show(this.modalCtrl);

		if (response) {
			try {
				await Api.forgotPassword(response);
				this.snackbarCtrl.open(this.translate.instant('reset_password.success'));
			} catch (err) {
				this.snackbarCtrl.open(this.translate.instant('reset_password.error'));
				console.log(err);
			}
		}
	}

	async showNewPasswordDialog(resetToken: string) {
		const passwordResetAlert = await this.alertCtrl.create({
			cssClass: 'password-reset',
			header: this.translate.instant('new_password.header'),
			message: this.translate.instant('new_password.msg'),
			inputs: [
				{
					name: 'password1',
					placeholder: this.translate.instant('new_password.password'),
					type: 'password',
					cssClass: 'alert-password-field',
				},
				{
					name: 'password2',
					placeholder: this.translate.instant('new_password.re_password'),
					type: 'password',
					cssClass: 'alert-password-field',
				},
			],
			buttons: [
				{
					text: this.translate.instant('new_password.cancel_btn'),
					role: 'cancel',
					cssClass: 'secondary',
				},
				{
					text: this.translate.instant('new_password.ok_btn'),
					cssClass: 'alert-button-ok',
					handler: async result => {
						if (result.password1 !== result.password2) {
							this.snackbarCtrl.open(this.translate.instant('new_password.password_match_error'), null, {
								duration: 2000,
							});
							await delay(2000);
							await this.showNewPasswordDialog(resetToken);
							return;
						}
						if (!ValidationUtils.validatePassword(result.password1)) {
							this.snackbarCtrl.open(this.translate.instant('new_password.password_error'), null, {
								duration: 6000,
							});
							await delay(2000);
							await this.showNewPasswordDialog(resetToken);
							return;
						}
						try {
							await Api.resetPassword(resetToken, result.password1);
							await this.navigationService.signIn();
							this.snackbarCtrl.open(this.translate.instant('new_password.success'), null, {
								duration: 2000,
							});
						} catch (e) {
							this.snackbarCtrl.open(this.translate.instant('new_password.error') + e, null, {
								duration: 2000,
							});
						}
					},
				},
			],
		});
		await passwordResetAlert.present();
	}

	goBack() {
		if (this.repository._order === null) {
			this.navigationService.home();
		} else {
			this.navigationService.menu();
		}
	}

	async signInWithGoogle() {
		if (!this.loading) {
			this.loading = true;
			try {
				const result = (await Api.signIn(AuthStrategy.GOOGLE)).data;
				window.location.href = result.url;
			} catch (e) {
				console.error(e);
			}
			this.loading = false;
		}
	}

	async signInWithFacebook() {
		// if (!this.loading) {
		// 	this.loading = true;
		// 	try {
		// 		const result = (await Api.signIn(AuthStrategy.FACEBOOK)).data;
		// 		window.location.href = result.url;
		// 	} catch (e) {
		// 		console.error(e);
		// 	}
		// 	this.loading = false;
		// }
	}

	async signInWithApple() {
		if (!this.loading) {
			this.loading = true;
			try {
				const result = (await Api.signIn(AuthStrategy.APPLE)).data;
				window.location.href = result.url;
			} catch (e) {
				console.error(e);
			}
			this.loading = false;
		}
	}
	dismiss() {
		this.modalCtrl.dismiss();
	}
}
